import React, { useEffect, useState } from 'react';
import { formatPersonLabel, personFocusNames, uniqueNameList } from '../../utils/personNames';
import './BookSheet.css';

/**
 * Writer-initiated same-person merge (#980). She picks two offers or cards
 * and confirms. No AI-suggested pairs. No nickname dictionary.
 */
export default function SamePersonMerge({ items = [], onCancel, onConfirm }) {
  const [firstId, setFirstId] = useState('');
  const [secondId, setSecondId] = useState('');
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');

  const first = items.find((item) => item.id === firstId) || null;
  const second = items.find((item) => item.id === secondId) || null;

  useEffect(() => {
    if (!first || !second) return;
    setName(first.primaryName || first.label);
    setNickname(second.nickname || second.primaryName || second.label);
  }, [first, second]);

  const canConfirm = first && second && first.id !== second.id && (name || '').trim();

  const handleConfirm = () => {
    if (!canConfirm) return;
    const primary = name.trim();
    const nick = nickname.trim();
    onConfirm({
      name: primary,
      nickname: nick,
      label: formatPersonLabel(primary, nick),
      alsoKnownAs: personFocusNames({
        name: primary,
        nickname: nick,
        alsoKnownAs: uniqueNameList([
          ...(first.alsoKnownAs || []),
          ...(second.alsoKnownAs || []),
          first.primaryName || first.label,
          second.primaryName || second.label,
          first.nickname,
          second.nickname,
        ]),
      }),
      keepId: first.id,
      absorbId: second.id,
      keep: first,
      absorb: second,
    });
  };

  return (
    <div className="bs-modal-backdrop" onClick={onCancel}>
      <div
        className="bs-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Same person"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bs-modal__header">
          <h2>Same person</h2>
          <button className="bs-modal__close" onClick={onCancel} aria-label="Close">&times;</button>
        </div>
        <p className="bs-modal__intro">
          Pick the two names that are the same person. You decide — nothing is merged until you confirm.
        </p>
        <div className="bs-edit__field">
          <label className="bs-edit__label" htmlFor="bs-merge-first">First name (becomes Name)</label>
          <select
            id="bs-merge-first"
            className="bs-edit__input"
            value={firstId}
            onChange={(e) => setFirstId(e.target.value)}
          >
            <option value="">Select a name…</option>
            {items.map((item) => (
              <option key={item.id} value={item.id} disabled={item.id === secondId}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="bs-edit__field">
          <label className="bs-edit__label" htmlFor="bs-merge-second">Second name (becomes Nickname)</label>
          <select
            id="bs-merge-second"
            className="bs-edit__input"
            value={secondId}
            onChange={(e) => setSecondId(e.target.value)}
          >
            <option value="">Select a name…</option>
            {items.map((item) => (
              <option key={`b-${item.id}`} value={item.id} disabled={item.id === firstId}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        {first && second && (
          <>
            <div className="bs-edit__field">
              <label className="bs-edit__label" htmlFor="bs-merge-name">Name</label>
              <input
                id="bs-merge-name"
                className="bs-edit__input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="bs-edit__field">
              <label className="bs-edit__label" htmlFor="bs-merge-nickname">Nickname</label>
              <input
                id="bs-merge-nickname"
                className="bs-edit__input"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
              />
            </div>
            <p className="bs-modal__intro">
              They will show as {formatPersonLabel(name, nickname) || 'one person'}.
            </p>
          </>
        )}
        <div className="bs-modal__actions">
          <button type="button" className="bs-btn bs-btn--primary" onClick={handleConfirm} disabled={!canConfirm}>
            Confirm same person
          </button>
          <button type="button" className="bs-btn bs-btn--secondary" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
